import { FieldPath } from 'react-hook-form';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { SubscriptionSettingsFormValues } from 'assets/js/pages/account-settings/account-about/givebutter-plus/types';
import * as Form from 'components/form';
import type { UseFormRadioGroupProps } from 'components/form/UseFormRadioGroup';
import * as Tooltip from 'components/tooltip';
import { CONTACT_US_LINK } from 'constants/links';
import { posthogEvents } from 'constants/posthog-events';
import { usePosthog } from 'customHooks/usePosthog';
import { twMerge } from 'tailwind-merge';

interface SubscriptionIntervalRadioGroupProps
    extends UseFormRadioGroupProps<
        SubscriptionSettingsFormValues,
        FieldPath<SubscriptionSettingsFormValues>
    > {}
const SubscriptionIntervalRadioGroup = ({
    className,
    ...rest
}: SubscriptionIntervalRadioGroupProps) => {
    return (
        <Form.UseFormRadioGroup
            className={twMerge('border border-gray-200 bg-gray-100', className)}
            variant={'toggle'}
            {...rest}
        />
    );
};

interface SubscriptionIntervalProps
    extends Omit<RadioGroup.IRadioGroupItemProps, 'value' | 'children'> {}
const SubscriptionIntervalMonthly = ({
    className,
    disabled,
    onClick,
    ...rest
}: SubscriptionIntervalProps) => {
    const { pushEvent } = usePosthog();
    return (
        <RadioGroup.Item
            className={twMerge('text-gray-400 disabled:opacity-100')}
            disabled={disabled}
            value={'month'}
            onClick={e => {
                pushEvent(posthogEvents.DASHBOARD_BILLING_BILLING_CYCLE_SELECTED, {
                    billingCycle: 'month',
                });
                onClick?.(e);
            }}
            {...rest}
        >
            <Tooltip.Root open={disabled ? undefined : false}>
                <Tooltip.Trigger asChild>
                    <div className="radix-group-disabled:opacity-50 flex w-full items-center justify-center gap-1.5 px-4">
                        <p>Monthly</p>
                    </div>
                </Tooltip.Trigger>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    <p className="!text-sm">
                        Switching from an annual to monthly Givebutter Plus plan is currently not
                        available. Please{' '}
                        <a
                            href={CONTACT_US_LINK}
                            target="_blank"
                            rel="noreferrer"
                            className="underline hover:text-white"
                        >
                            {' '}
                            contact us
                        </a>{' '}
                        for assistance.
                    </p>
                </Tooltip.Content>
            </Tooltip.Root>
        </RadioGroup.Item>
    );
};

const SubscriptionIntervalAnnually = ({
    className,
    onClick,
    ...rest
}: SubscriptionIntervalProps) => {
    const { pushEvent } = usePosthog();
    return (
        <RadioGroup.Item
            className={'text-gray-400'}
            value={'year'}
            onClick={e => {
                pushEvent(posthogEvents.DASHBOARD_BILLING_BILLING_CYCLE_SELECTED, {
                    billingCycle: 'year',
                });
                onClick?.(e);
            }}
            {...rest}
        >
            <div className="flex w-full items-center justify-center gap-1.5 px-4">
                <p>Annual</p>
                <span className="text-brand-violet-primary text-nowrap">(Best value!)</span>
            </div>
        </RadioGroup.Item>
    );
};

const Root = SubscriptionIntervalRadioGroup;
const Monthly = SubscriptionIntervalMonthly;
const Annually = SubscriptionIntervalAnnually;

export {
    Root,
    Monthly,
    Annually,
    //
    SubscriptionIntervalRadioGroup,
    SubscriptionIntervalMonthly,
    SubscriptionIntervalAnnually,
};
